import React from "react";
import { useFormikContext } from "formik";
import { FormikInputWithLabel } from "newui/components/ui/InputWithLabel/FormikInputWithLabel";
import { FormikImagePreview } from "newui/components/ui/ImagePreview";
import { SectorSelect } from "newui/components/ui/CommonSelects/SectorSelect";
import { ExpertiseRepeater } from "./ExpertiseRepeater";
import type { KeyPersonnelFormValues } from "../types/KeyPersonnel.type";
import type { SelectOption } from "newui/components/ui/InputWithLabel/SelectWithLabel";

interface KeyPersonnelFormProps {
	children?: React.ReactNode;
	profileTypeId: number;
}

const PROFILE_TYPE_CONSULTANT = 4;

const KeyPersonnelForm: React.FC<KeyPersonnelFormProps> = ({
	profileTypeId,
	children,
}) => {
	const { values, setFieldValue } = useFormikContext<KeyPersonnelFormValues>();

	const handleSectorChange = (selected: SelectOption | SelectOption[] | null) => {
		const ids = Array.isArray(selected) ? selected.map((o) => Number(o.value)) : [];
		setFieldValue("sector_ids", ids, true);
	};

	return (
		<div className="flex flex-col gap-6 px-2" data-testid="key-personnel-form">
			{/* Avatar */}
			<FormikImagePreview
				label="Photo"
				name="avatar_image"
				testId="avatar-upload"
			/>

			{/* Name row */}
			<div className="grid grid-cols-1 md:grid-cols-2 gap-6">
				<FormikInputWithLabel
					label="First Name"
					name="first_name"
					required
					testId="first-name-input"
				/>
				<FormikInputWithLabel
					label="Last Name"
					name="last_name"
					required
					testId="last-name-input"
				/>
			</div>

			<div className="grid grid-cols-1 md:grid-cols-2 gap-6">
				{/* Email */}
				<FormikInputWithLabel
					label="Email"
					name="email"
					type="email"
					testId="email-input"
					required
				/>

				{/* Position */}
				<FormikInputWithLabel
					label="Current Position"
					name="position"
					required
					testId="position-input"
				/>
			</div>

			{/* Experience & Sort Order */}
			<div className="grid grid-cols-1 md:grid-cols-2 gap-6">
				<FormikInputWithLabel
					label="Industry Years of Experience"
					name="experience"
					type="number"
					testId="experience-input"
					min={0}
					required
					guideLine="Updates automatically every year"
				/>
				{values.sort_order !== null && (
					<FormikInputWithLabel
						label="Sort Order"
						name="sort_order"
						type="number"
						testId="sort-order-input"
						min={1}
						guideLine="This controls the order of personnel displayed, with 1 being the top. Adjust as needed to reorder."
					/>
				)}
			</div>

			{/* Sectors */}
			<SectorSelect
				label="Top 3 Sectors"
				name="sector_ids"
				isMulti
				selectedIds={values.sector_ids}
				onChange={handleSectorChange}
				testId="sectors-select"
				required
				maxSelect={3}
				placeholder="Select up to 3 sectors"
			/>

			{/* Expertise/Disciplines — consultant only */}
			{profileTypeId === PROFILE_TYPE_CONSULTANT && (
				<div>
					<label className="mb-2 block text-sm font-medium text-gray-700">
						Expertise & Disciplines
					</label>
					<ExpertiseRepeater
						name="disciplines"
						profileTypeId={profileTypeId}
					/>
				</div>
			)}

			{/* Slot for ProjectExperienceTable and ResumeTable */}
			{children}
		</div>
	);
};

export { KeyPersonnelForm };
export type { KeyPersonnelFormProps };
